import { User } from 'lucide-react'

import { AccountTopBar } from '@/components/sidebar'
import { Skeleton } from '@/components/ui/skeleton'
import { appHref, marketingHref } from '@/config/config'
import { cn } from '@/lib/utils'
import {
  type MatchedBroker,
  MatchedBrokerMobileBar,
  MatchedBrokerNavCluster,
} from './MatchedBrokerNavCluster'
import { NavBar, type NavBarTheme } from './NavBar'
import { NavCartButton } from './NavCartButton'

export interface AccountNavSlots {
  /** Opens the side nav on phones, where it is a drawer. */
  menuButton?: React.ReactNode
  /** The customer's name, as the org switcher. */
  org: React.ReactNode
  /** The product tabs, centred on the bar; the phone drawer holds them below md. */
  tabs?: React.ReactNode
  /** The signed-in person's menu, at the far right. */
  profile: React.ReactNode
}

/**
 * Stripped-back nav for product flows (funnels, applications) — logo and
 * phone/login utility links only. No in-page nav links and no primary CTA;
 * the flow itself is the call to action.
 */
export function ProductFlowNav({
  disableScrollHide,
  hideLogin,
  matchedBroker,
  matchedBrokerPending,
  allowBrokerNote,
  cartCount,
  onCartClick,
  account,
  logoHref = marketingHref('/'),
  rightAction,
  accountMenu,
  containerAligned,
  theme = 'light',
}: {
  disableScrollHide?: boolean
  /** Drop the "Login" utility link — use on routes only reachable while already signed in. */
  hideLogin?: boolean
  /** Replaces the phone/login utility links with a matched-broker card — for pages built for an already-matched customer (e.g. the Quotes page). */
  matchedBroker?: MatchedBroker
  /** The broker is still loading: hold the broker nav's height so it doesn't grow under the page. */
  matchedBrokerPending?: boolean
  /** Adds the "Leave a note" button to the matched-broker pill and mobile bar. Signed-in pages only: the note goes out as the current user. */
  allowBrokerNote?: boolean
  /** When set, renders a cart button in the nav with this count badge. */
  cartCount?: number
  onCartClick?: () => void
  /** The signed-in customer in their account: their org, the product tabs and their menu, edge to edge. */
  account?: AccountNavSlots
  /** Destination for the logo link. Defaults to the marketing home, whichever host the flow is on; `null` renders the logo unlinked. */
  logoHref?: string | null
  /** A page action (e.g. Share), rendered at the right end of the nav, with or without a matched broker. */
  rightAction?: React.ReactNode
  /** The signed-in account menu (e.g. `<LoginButtons trigger="menu" />`), at the far right. Signed-in pages only. */
  accountMenu?: React.ReactNode
  /** Line the nav up with the page's `<Container>` — for app pages whose body sits in one. */
  containerAligned?: boolean
  /** Dark inverts the bar for pages on an ink-900 ground. */
  theme?: NavBarTheme
}) {
  const actions =
    rightAction || cartCount !== undefined || accountMenu ? (
      <>
        {rightAction}
        {cartCount !== undefined && <NavCartButton count={cartCount} onClick={onCartClick} />}
        {accountMenu}
      </>
    ) : undefined

  if (account) {
    return (
      <AccountTopBar
        home={logoHref ? { href: logoHref, label: 'Cai home' } : undefined}
        menuButton={account.menuButton}
        org={account.org}
        tabs={account.tabs}
        actions={rightAction}
        profile={account.profile}
      />
    )
  }

  if (matchedBrokerPending) {
    return (
      <>
        <NavBar
          theme={theme}
          className={cn(
            'md:h-18',
            theme === 'dark' ? 'bg-ink-900 border-ink-700' : 'bg-ink-50 border-ink-100'
          )}
          disableScrollHide={disableScrollHide}
          rightSlot={<span />}
          actions={actions}
          logoHref={logoHref}
          containerAligned={containerAligned}
        />
        {/* The mobile broker bar's height: its pill is a 36px avatar inside py-1.5 and a border. */}
        <div
          className={cn(
            'border-ink-100 bg-ink-50 w-full border-b px-2.75 py-2 md:hidden',
            containerAligned && 'px-2'
          )}
        >
          <Skeleton className="h-[50px] w-full rounded-full" />
        </div>
      </>
    )
  }

  if (matchedBroker) {
    return (
      <>
        <NavBar
          theme={theme}
          // The broker pill is the tallest thing any nav holds, and md:h-16 left it flush.
          className={cn(
            'md:h-18',
            theme === 'dark' ? 'bg-ink-900 border-ink-700' : 'bg-ink-50 border-ink-100'
          )}
          disableScrollHide={disableScrollHide}
          rightSlot={<MatchedBrokerNavCluster broker={matchedBroker} allowNote={allowBrokerNote} />}
          actions={actions}
          logoHref={logoHref}
          containerAligned={containerAligned}
        />
        <MatchedBrokerMobileBar
          broker={matchedBroker}
          allowNote={allowBrokerNote}
          className={containerAligned ? 'px-2' : undefined}
        />
      </>
    )
  }

  return (
    <NavBar
      theme={theme}
      phoneNumber="(770) 765-0331"
      utilityLinks={hideLogin ? [] : [{ label: 'Login', icon: <User />, href: appHref('/insure') }]}
      className={theme === 'dark' ? 'bg-ink-900' : 'bg-ink-50'}
      disableScrollHide={disableScrollHide}
      logoHref={logoHref}
      actions={actions}
      containerAligned={containerAligned}
    />
  )
}
